import { config, getApiUrl } from '../config';

export interface ImprovePromptRequest {
  prompt: string;
  user_context?: Record<string, string>;
  use_free_tier?: boolean;
  user_id?: string;
}

export interface ImprovePromptResponse {
  structural_improvement: string;
  context_questions?: Array<{
    id: string;
    question: string;
    suggestions: string[];
  }>;
  personalized_prompt?: string;
  error?: string;
  mad_libs_template?: {
    template: string;
    variables: Record<string, string[]>;
  };
}

export interface AuthSignupRequest {
  email: string;
  password: string;
  username?: string;
  sync_enabled?: boolean;
  analytics_enabled?: boolean;
}

export interface AuthLoginRequest {
  email: string;
  password: string;
}

export interface AuthTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserProfile {
  id: string;
  email: string;
  username?: string;
  email_verified: boolean;
  subscription_tier: string;
  sync_enabled: boolean;
  analytics_enabled: boolean;
  created_at: string;
}

export interface UserSettings {
  api_endpoint: string;
  user_category?: string;
  system_prompt?: string;
  show_preview: boolean;
  auto_improve: boolean;
  theme: string;
  language: string;
  last_synced_at?: string;
  sync_version: number;
}

export interface UpdateSettingsRequest {
  api_endpoint?: string;
  user_category?: string;
  system_prompt?: string;
  show_preview?: boolean;
  auto_improve?: boolean;
  theme?: string;
  language?: string;
}

class ApiService {
  private authToken: string | null = null;

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    return headers;
  }

  async get(endpoint: string): Promise<any> {
    const response = await this.fetchWithTimeout(
      getApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }

  async post(endpoint: string, data: any): Promise<any> {
    const response = await this.fetchWithTimeout(
      getApiUrl(endpoint),
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }

  async put(endpoint: string, data: any): Promise<any> {
    const response = await this.fetchWithTimeout(
      getApiUrl(endpoint),
      {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }

  async delete(endpoint: string): Promise<any> {
    const response = await this.fetchWithTimeout(
      getApiUrl(endpoint),
      {
        method: 'DELETE',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number = 30000
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async improvePrompt(request: ImprovePromptRequest): Promise<ImprovePromptResponse> {
    try {
      const response = await this.fetchWithTimeout(
        getApiUrl(config.api.endpoints.improvePrompt),
        {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to improve prompt:', error);
      throw error;
    }
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await this.fetchWithTimeout(
        getApiUrl(config.api.endpoints.health),
        { method: 'GET' },
        5000
      );
      return response.ok;
    } catch {
      return false;
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  auth = {
    signup: async (request: AuthSignupRequest): Promise<AuthTokenResponse> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/signup'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Signup failed');
      }

      const data = await response.json();
      // Extract token response from signup response
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: data.token_type,
        expires_in: data.expires_in
      };
    },

    login: async (request: AuthLoginRequest): Promise<AuthTokenResponse> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/login'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      return await response.json();
    },

    logout: async (): Promise<void> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/logout'),
        {
          method: 'POST',
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Logout failed');
      }

      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_email');
    },

    getProfile: async (): Promise<{ user: UserProfile; settings: UserSettings }> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/me'),
        {
          method: 'GET',
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      return await response.json();
    },

    getSettings: async (): Promise<UserSettings> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/settings'),
        {
          method: 'GET',
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      return await response.json();
    },

    updateSettings: async (settings: UpdateSettingsRequest): Promise<UserSettings> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/settings'),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify(settings),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      return await response.json();
    },

    refreshToken: async (refreshToken: string): Promise<AuthTokenResponse> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/refresh'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      return await response.json();
    },

    deleteAccount: async (): Promise<void> => {
      const response = await this.fetchWithTimeout(
        getApiUrl('/api/auth/account'),
        {
          method: 'DELETE',
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }
    }
  };
}

export const api = new ApiService();
export const apiService = api; // For backward compatibility
export const apiClient = api; // Another alias for backward compatibility